// src/context/authContext.tsx

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { getMe, refreshSession } from '../api/auth';

export interface AuthData {
  userId: string;
  username: string;
  email: string;
  fullName?: string;
  organizationId: string;
  organizationName: string;
  organizationSlug: string;
  isAdmin: boolean;
  isInternalAdmin?: boolean;
  emailVerified: boolean;
}

type AuthContextType = {
  authData?: AuthData;
  authReady: boolean;
  setAuthData: (data?: AuthData) => void;
};

export const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [authData, setAuthDataState] = useState<AuthData | undefined>(() => {
    try {
      const stored = localStorage.getItem('authData');
      return stored ? JSON.parse(stored) : undefined;
    } catch {
      localStorage.removeItem('authData');
      return undefined;
    }
  });
  const [authReady, setAuthReady] = useState(false);

  const setAuthData = (data?: AuthData) => {
    setAuthDataState(data);
    if (data) {
      localStorage.setItem('authData', JSON.stringify(data));
    } else {
      localStorage.removeItem('authData');
    }
  };

  useEffect(() => {
    void (async () => {
      try {
        let me;
        try {
          me = await getMe();
        } catch {
          await refreshSession();
          me = await getMe();
        }
        const next: AuthData = {
          userId: me.id,
          username: me.username,
          email: me.email,
          fullName: me.full_name,
          organizationId: me.organization_id || '',
          organizationName: me.organization_name || '',
          organizationSlug: me.organization_slug || '',
          isAdmin: Boolean(me.is_admin ?? me.admin),
          isInternalAdmin: me.is_internal_admin,
          emailVerified: Boolean(me.email_verified),
        };
        setAuthData(next);
      } catch {
        setAuthData(undefined);
      } finally {
        setAuthReady(true);
      }
    })();
  }, []);

  return (
    <AuthContext.Provider value={{ authData, authReady, setAuthData }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
